import React from 'react'
import {
  Col,
  Container,
  FileDropzone,
  Form,
  Row,
  Spinner,
  useToaster
} from 'react-angle-dashboard-components'
import { useFormContext } from 'react-hook-form'
import { IQuoteFormData } from '../../QuoteForm'
import { GrayText, GrayTextLg, Heading, SmallGrayHeading } from '../../style'

import FooterNav from '../../FooterNav'
import { ReactComponent as ExclaimationImg } from '../../../../assets/exclaimationCircle.svg'
import {
  useDeleteUploadedFile,
  useGetExperienceFiles,
  useGetMonthlyInvoice,
  useUploadFile
} from '../../../../hooks/quote.hooks'
import { IFileType, IGetFileDTO } from '../../../../services/quote/dto'

const QuoteDetailsStep2 = () => {
  const { showError } = useToaster()
  const { register, formState, watch, setValue } =
    useFormContext<IQuoteFormData>()

  const quoteId = watch('entryId')

  const { data: experienceFiles } = useGetExperienceFiles(quoteId?.toString())
  const { data: monthlyInvoiceFiles } = useGetMonthlyInvoice(
    quoteId?.toString()
  )

  const { mutateAsync: uploadFiles } = useUploadFile()
  const { mutateAsync: deleteFile } = useDeleteUploadedFile()

  const errors = formState.errors.quoteDetails?.step2

  const changeUploadedFiles = async (
    files: File[],
    currentFiles: IGetFileDTO[] | undefined,
    fileType: IFileType
  ) => {
    if (quoteId) {
      try {
        const newFiles = files.filter(
          (f) =>
            (currentFiles?.findIndex((ef) => ef.fileName === f.name) ?? -1) ===
            -1
        )
        const removedFiles =
          currentFiles?.filter(
            (ef) =>
              (files?.findIndex((f) => ef.fileName === f.name) ?? -1) === -1
          ) ?? []

        await uploadFiles({
          quoteId,
          fileType,
          files: newFiles
        })
        await Promise.all(
          removedFiles.map((rf) => deleteFile({ quoteId, fileId: rf.entryId }))
        )
      } catch (e) {
        console.error(e)
        showError(
          'There was a problem saving your changes. Please refresh the page and try again.'
        )
      }
    }
  }

  const onExperienceInfoLoaded = async (files: File[]) => {
    setValue('quoteDetails.step2.experienceFilesUploaded', files.length > 0)
    return changeUploadedFiles(files, experienceFiles, 'experience_info')
  }

  const onMonthlyInvoiceLoaded = (files: File[]) => {
    setValue('quoteDetails.step2.monthlyInvoiceUploaded', files.length > 0)
    return changeUploadedFiles(files, monthlyInvoiceFiles, 'monthly_invoice')
  }

  return (
    <Container className="p-4">
      {!experienceFiles || !monthlyInvoiceFiles ? (
        <Spinner animation="border" />
      ) : (
        <>
          <Row>
            <Col sm={4}>
              <SmallGrayHeading>Quote Details (2/2)</SmallGrayHeading>
              <Heading className="pt-1 pb-3">Add Quote Details</Heading>
              <Form.Group>
                <Form.Label>Experience Information</Form.Label>
                <FileDropzone
                  onFileLoaded={onExperienceInfoLoaded}
                  initialFiles={experienceFiles?.map<File>(
                    (f) => new File([], `${f.fileName}`)
                  )}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Total Current Monthly Health Plan Spend</Form.Label>
                <Form.Control
                  isInvalid={!!errors?.currentMonthlySpend}
                  {...register('quoteDetails.step2.currentMonthlySpend')}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.currentMonthlySpend?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Total Renewal Monthly Health Plan Spend</Form.Label>
                <Form.Control
                  isInvalid={!!errors?.renewalMonthlySpend}
                  {...register('quoteDetails.step2.renewalMonthlySpend')}
                />
                <Form.Control.Feedback type="invalid">
                  {errors?.renewalMonthlySpend?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>Monthly Invoice Example</Form.Label>
                <FileDropzone onFileLoaded={onMonthlyInvoiceLoaded} />
              </Form.Group>
            </Col>
            <Col className="ml-10">
              <div
                className="p-4"
                style={{
                  maxWidth: 580,
                  backgroundColor: '#F6F2EC'
                }}
              >
                <Col>
                  <Row>
                    <ExclaimationImg
                      fill="#EDCA60"
                      style={{
                        width: 36,
                        height: 36,
                        overflow: 'visible',
                        marginBottom: 10
                      }}
                    />
                  </Row>
                  <Row>
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: 20
                      }}
                    >
                      Quote Details - Tips
                    </span>
                  </Row>
                  <Row className="mt-2">
                    <GrayTextLg>
                      Submitting experience and invoice information greatly
                      helps us gather comprehensive details for your group,
                      ensuring the most competitive quote.
                    </GrayTextLg>
                  </Row>
                </Col>
                <Row className="my-2">
                  <Col className="col-auto pr-0">
                    <ExclaimationImg
                      fill="#EDCA60"
                      style={{
                        width: 20,
                        height: 20,
                        overflow: 'visible',
                        marginBottom: 10
                      }}
                    />
                  </Col>
                  <Col>
                    <div
                      style={{
                        color: '#212529',
                        fontSize: 16,
                        fontWeight: 600
                      }}
                    >
                      Large groups (150+) and previously level funded groups are
                      strongly encouraged to provide experience information.
                      Without it, we cannot guarantee a competitive quote.
                    </div>
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col className="col-auto pr-0">
                    <ExclaimationImg
                      fill="#6D39D9"
                      style={{
                        width: 20,
                        height: 20,
                        overflow: 'visible',
                        marginBottom: 10
                      }}
                    />
                  </Col>
                  <Col>
                    <GrayTextLg>
                      If you're currently unable to provide this information,
                      you have the option to save your quote as a draft. You can
                      return at a later time to input the details.
                      Alternatively, you can proceed with the quote without this
                      information and make edits once you receive the initial
                      quote.
                    </GrayTextLg>
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col className="col-auto pr-0">
                    <ExclaimationImg
                      fill="#6D39D9"
                      style={{
                        width: 20,
                        height: 20,
                        overflow: 'visible',
                        marginBottom: 10
                      }}
                    />
                  </Col>
                  <Col>
                    <GrayTextLg>
                      Once you submit experience and invoice information, our
                      team will carefully review the submitted details. Please
                      allow up to 24-48 hours for this review process. Once
                      complete, we will update the status of your quote to
                      “Quoted” and promptly notify you via email that your quote
                      is ready for review.
                    </GrayTextLg>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Form.Group
            className="mt-5"
            style={{
              maxWidth: 820
            }}
          >
            <Form.Check>
              <Form.Check.Input
                isInvalid={!!errors?.acknowledgment}
                {...register('quoteDetails.step2.acknowledgment')}
              />
              <Form.Check.Label>
                I hereby acknowledge that the information I have provided is
                true to the best of my knowledge and belief.
              </Form.Check.Label>
              <GrayText
                style={{
                  fontSize: 16
                }}
              >
                I understand that by not providing accurate and complete
                information, I may not receive the best quote available for the
                services or products I am seeking. I further understand that any
                quote provided to me is based on the information I have
                provided, and any discrepancies or omissions in information may
                result in a revised or different quote.
              </GrayText>
            </Form.Check>
            <Form.Control.Feedback type="invalid">
              {errors?.acknowledgment?.message}
            </Form.Control.Feedback>
          </Form.Group>
          <FooterNav triggerPath="quoteDetails.step2" />
        </>
      )}
    </Container>
  )
}

export default QuoteDetailsStep2
